/**
 * Conta Digital (wallet) UI translations.
 * Language is derived from @/config/locale (LOCALE_CONFIG.language).
 */

import { LOCALE_CONFIG } from "@/config/locale";

const messages = {
  pt: {
    // Page
    pageTitle: "Conta Digital",
    back: "Voltar",
    loading: "Carregando...",
    errorLoadWallet: "Não foi possível carregar a conta digital.",
    errorOpenSetup: "Não foi possível abrir a configuração.",
    errorOpenUpdate: "Não foi possível abrir a atualização.",
    errorStripePublicUrlUser:
      "Não foi possível abrir o cadastro de pagamentos. Defina a URL pública do site (VITE_PRD_PUBLIC_APP_URL) ou abra pelo domínio https correto.",
    errorStripePublicUrlDev:
      "Desenvolvimento: em .env defina VITE_PRD_PUBLIC_APP_URL com a origem https do site (ex.: https://www.akihub.com.br). O Stripe não aceita retorno em capacitor://.",
    setupIntro:
      "Configure sua conta para receber pagamentos das suas vendas. Você poderá informar sua conta bancária e acompanhar saldo e saques.",
    setupCta: "Configurar conta para receber",
    opening: "Abrindo...",
    updatePayoutDetails: "Atualizar dados de pagamento",
    // Verification / status
    completeVerification: "Complete sua verificação",
    verificationHint:
      "Algumas informações são necessárias para habilitar saques. Atualize seus dados de pagamento.",
    payoutsSuspended: "Saques temporariamente suspensos",
    reason: "Motivo",
    updateToReactivate: "Atualize seus dados de pagamento para reativar.",
    scheduleDaily: "Seu saldo é pago automaticamente todo dia.",
    scheduleWeekly: "Saques automáticos semanais.",
    scheduleMonthly: "Saques automáticos mensais.",
    lastPayoutFailed: "Último saque falhou",
    payoutErrorFallback: "Erro ao processar saque.",
    amount: "Valor",
    nextPayout: "Próximo saque",
    nextPayoutDatePreposition: "em",
    payoutAccount: "Conta de saque",
    noBankAccount: "Nenhuma conta bancária cadastrada para saque.",
    // Balance card
    availableBalance: "Saldo Disponível",
    hideBalance: "Ocultar saldo",
    showBalance: "Mostrar saldo",
    pending: "Pendente",
    inReserve: "Em reserva",
    inEscrow: "Em garantia",
    withdraw: "Sacar",
    addFunds: "Adicionar",
    // Quick stats
    toReceive: "A Receber",
    toPay: "A Pagar",
    pendingCount: "Pendentes",
    // Transactions
    transactions: "Transações",
    noTransactions: "Nenhuma transação encontrada",
    transactionDefaultTitle: "Transação",
    // Stripe balance transaction types (see https://docs.stripe.com/reports/balance-transaction-types)
    transactionTypePayment: "Venda",
    transactionTypeCharge: "Cobrança",
    transactionTypePayout: "Saque",
    transactionTypeRefund: "Reembolso",
    transactionTypeApplicationFee: "Taxa de Proteção e Validação.",
    transactionTypeApplicationFeeRefund: "Reembolso da Taxa de Proteção e Validação",
    transactionTypeAdjustment: "Ajuste",
    transactionTypePaymentRefund: "Reembolso de pagamento",
    transactionTypePaymentReversal: "Estorno de pagamento",
    transactionTypePaymentFailureRefund: "Reembolso por falha no pagamento",
    transactionTypeRefundFailure: "Falha no reembolso",
    transactionTypePayoutFailure: "Falha no saque",
    transactionTypePayoutCancel: "Saque cancelado",
    transactionTypeTransfer: "Transferência",
    transactionTypeTransferCancel: "Transferência cancelada",
    transactionTypeTransferFailure: "Falha na transferência",
    transactionTypeTransferRefund: "Reembolso de transferência",
    transactionTypeTopup: "Depósito",
    transactionTypeTopupReversal: "Estorno de depósito",
    transactionTypeStripeFee: "Taxa Stripe",
    transactionTypeStripeFxFee: "Taxa de câmbio",
    transactionTypeTaxFee: "Taxa de imposto",
    transactionTypeAdvance: "Antecipação",
    transactionTypeAdvanceFunding: "Financiamento de antecipação",
    transactionTypeReserveHold: "Reserva (bloqueio)",
    transactionTypeReserveRelease: "Reserva (liberação)",
    transactionTypeReservedFunds: "Fundos em reserva",
    transactionTypeReserveTransaction: "Reserva",
    transactionTypeConnectCollectionTransfer: "Cobrança Connect",
    transactionTypePaymentUnreconciled: "Pagamento não conciliado",
    transactionTypeStripeBalancePaymentDebit: "Débito em conta Stripe",
    transactionTypeStripeBalancePaymentDebitReversal: "Estorno de débito em conta Stripe",
    transactionTypePaymentNetworkReserveHold: "Reserva da rede",
    transactionTypePaymentNetworkReserveRelease: "Liberação de reserva da rede",
    transactionTypePayoutMinimumBalanceHold: "Saldo mínimo retido",
    transactionTypePayoutMinimumBalanceRelease: "Liberação de saldo mínimo",
    transactionTypeCurrencyConversion: "Conversão de moeda",
    transactionTypeBalanceTransferOutbound: "Transferência de saldo (saída)",
    transactionTypeBalanceTransferInbound: "Transferência de saldo (entrada)",
    transactionTypeAnticipationRepayment: "Pagamento de antecipação",
    transactionTypeContribution: "Contribuição",
    transactionTypeClimateOrderPurchase: "Compra Climate",
    transactionTypeClimateOrderRefund: "Reembolso Climate",
    transactionTypeRevenueShare: "Participação em receita",
    transactionTypeObligationOutbound: "Obrigação (saída)",
    transactionTypeObligationReversalInbound: "Estorno de obrigação (entrada)",
    transactionTypeIssuingAuthorizationHold: "Reserva de autorização (cartão)",
    transactionTypeIssuingAuthorizationRelease: "Liberação de autorização (cartão)",
    transactionTypeIssuingTransaction: "Transação (cartão)",
    transactionTypeIssuingDispute: "Disputa (cartão)",
    statusCompleted: "Concluído",
    statusCancelled: "Cancelado",
    statusPending: "Pendente",
    availableOn: "Disponível em",
    // Alerts (withdraw / add funds)
    withdrawAlertTitle: "Saldo disponível para saque",
    withdrawComingSoon: "Em breve: Função de saque",
    addFundsComingSoon: "Em breve: Adicionar fundos à sua conta",
  },
  en: {
    pageTitle: "Digital Account",
    back: "Back",
    loading: "Loading...",
    errorLoadWallet: "Could not load the digital account.",
    errorOpenSetup: "Could not open setup.",
    errorOpenUpdate: "Could not open update.",
    errorStripePublicUrlUser:
      "Payment setup can't be opened. Set the public site URL (VITE_PRD_PUBLIC_APP_URL) or use the correct https origin.",
    errorStripePublicUrlDev:
      "Development: set VITE_PRD_PUBLIC_APP_URL in .env to your https site origin (e.g. https://www.example.com). Stripe does not accept capacitor:// return URLs.",
    setupIntro:
      "Set up your account to receive payments from your sales. You can add your bank account and track balance and payouts.",
    setupCta: "Set up account to receive payments",
    opening: "Opening...",
    updatePayoutDetails: "Update payout details",
    completeVerification: "Complete your verification",
    verificationHint:
      "Some information is required to enable payouts. Update your payout details.",
    payoutsSuspended: "Payouts temporarily suspended",
    reason: "Reason",
    updateToReactivate: "Update your payout details to reactivate.",
    scheduleDaily: "Your balance is paid out automatically every day.",
    scheduleWeekly: "Automatic weekly payouts.",
    scheduleMonthly: "Automatic monthly payouts.",
    lastPayoutFailed: "Last payout failed",
    payoutErrorFallback: "Error processing payout.",
    amount: "Amount",
    nextPayout: "Next payout",
    nextPayoutDatePreposition: "on",
    payoutAccount: "Payout account",
    noBankAccount: "No bank account set up for payouts.",
    availableBalance: "Available Balance",
    hideBalance: "Hide balance",
    showBalance: "Show balance",
    pending: "Pending",
    inReserve: "In reserve",
    inEscrow: "In escrow",
    withdraw: "Withdraw",
    addFunds: "Add funds",
    toReceive: "To receive",
    toPay: "To pay",
    pendingCount: "Pending",
    transactions: "Transactions",
    noTransactions: "No transactions found",
    transactionDefaultTitle: "Transaction",
    transactionTypePayment: "Sale",
    transactionTypeCharge: "Charge",
    transactionTypePayout: "Payout",
    transactionTypeRefund: "Refund",
    transactionTypeApplicationFee: "Platform fee",
    transactionTypeApplicationFeeRefund: "Application fee refund",
    transactionTypeAdjustment: "Adjustment",
    transactionTypePaymentRefund: "Payment refund",
    transactionTypePaymentReversal: "Payment reversal",
    transactionTypePaymentFailureRefund: "Payment failure refund",
    transactionTypeRefundFailure: "Refund failure",
    transactionTypePayoutFailure: "Payout failure",
    transactionTypePayoutCancel: "Payout cancelled",
    transactionTypeTransfer: "Transfer",
    transactionTypeTransferCancel: "Transfer cancelled",
    transactionTypeTransferFailure: "Transfer failure",
    transactionTypeTransferRefund: "Transfer refund",
    transactionTypeTopup: "Top-up",
    transactionTypeTopupReversal: "Top-up reversal",
    transactionTypeStripeFee: "Stripe fee",
    transactionTypeStripeFxFee: "Currency conversion fee",
    transactionTypeTaxFee: "Tax fee",
    transactionTypeAdvance: "Advance",
    transactionTypeAdvanceFunding: "Advance funding",
    transactionTypeReserveHold: "Reserve hold",
    transactionTypeReserveRelease: "Reserve release",
    transactionTypeReservedFunds: "Reserved funds",
    transactionTypeReserveTransaction: "Reserve",
    transactionTypeConnectCollectionTransfer: "Connect collection transfer",
    transactionTypePaymentUnreconciled: "Unreconciled payment",
    transactionTypeStripeBalancePaymentDebit: "Stripe balance debit",
    transactionTypeStripeBalancePaymentDebitReversal: "Stripe balance debit reversal",
    transactionTypePaymentNetworkReserveHold: "Payment network reserve hold",
    transactionTypePaymentNetworkReserveRelease: "Payment network reserve release",
    transactionTypePayoutMinimumBalanceHold: "Payout minimum balance hold",
    transactionTypePayoutMinimumBalanceRelease: "Payout minimum balance release",
    transactionTypeCurrencyConversion: "Currency conversion",
    transactionTypeBalanceTransferOutbound: "Balance transfer (outbound)",
    transactionTypeBalanceTransferInbound: "Balance transfer (inbound)",
    transactionTypeAnticipationRepayment: "Anticipation repayment",
    transactionTypeContribution: "Contribution",
    transactionTypeClimateOrderPurchase: "Climate order purchase",
    transactionTypeClimateOrderRefund: "Climate order refund",
    transactionTypeRevenueShare: "Revenue share",
    transactionTypeObligationOutbound: "Obligation (outbound)",
    transactionTypeObligationReversalInbound: "Obligation reversal (inbound)",
    transactionTypeIssuingAuthorizationHold: "Issuing authorization hold",
    transactionTypeIssuingAuthorizationRelease: "Issuing authorization release",
    transactionTypeIssuingTransaction: "Issuing transaction",
    transactionTypeIssuingDispute: "Issuing dispute",
    statusCompleted: "Completed",
    statusCancelled: "Cancelled",
    statusPending: "Pending",
    availableOn: "Available on",
    withdrawAlertTitle: "Available balance for withdrawal",
    withdrawComingSoon: "Coming soon: Withdrawal",
    addFundsComingSoon: "Coming soon: Add funds to your account",
  },
} as const;

export type WalletI18nKey = keyof (typeof messages)["pt"];

const lang: keyof typeof messages =
  String(LOCALE_CONFIG.language) === "en" ? "en" : "pt";

export function t(key: WalletI18nKey): string {
  return messages[lang][key] ?? (messages.pt as Record<string, string>)[key] ?? key;
}

export function stripeConnectConfigErrorMessage(): string {
  const user = t("errorStripePublicUrlUser");
  if (import.meta.env.DEV) {
    return `${user}\n\n${t("errorStripePublicUrlDev")}`;
  }
  return user;
}

/** Maps Stripe balance transaction type to a translated label. Types from https://docs.stripe.com/reports/balance-transaction-types */
const TRANSACTION_TYPE_KEYS: Record<string, WalletI18nKey> = {
  payment: "transactionTypePayment",
  charge: "transactionTypeCharge",
  payout: "transactionTypePayout",
  refund: "transactionTypeRefund",
  application_fee: "transactionTypeApplicationFee",
  application_fee_refund: "transactionTypeApplicationFeeRefund",
  adjustment: "transactionTypeAdjustment",
  payment_refund: "transactionTypePaymentRefund",
  payment_reversal: "transactionTypePaymentReversal",
  payment_failure_refund: "transactionTypePaymentFailureRefund",
  refund_failure: "transactionTypeRefundFailure",
  payout_failure: "transactionTypePayoutFailure",
  payout_cancel: "transactionTypePayoutCancel",
  transfer: "transactionTypeTransfer",
  transfer_cancel: "transactionTypeTransferCancel",
  transfer_failure: "transactionTypeTransferFailure",
  transfer_refund: "transactionTypeTransferRefund",
  topup: "transactionTypeTopup",
  topup_reversal: "transactionTypeTopupReversal",
  stripe_fee: "transactionTypeStripeFee",
  stripe_fx_fee: "transactionTypeStripeFxFee",
  tax_fee: "transactionTypeTaxFee",
  advance: "transactionTypeAdvance",
  advance_funding: "transactionTypeAdvanceFunding",
  reserve_hold: "transactionTypeReserveHold",
  reserve_release: "transactionTypeReserveRelease",
  reserved_funds: "transactionTypeReservedFunds",
  reserve_transaction: "transactionTypeReserveTransaction",
  connect_collection_transfer: "transactionTypeConnectCollectionTransfer",
  payment_unreconciled: "transactionTypePaymentUnreconciled",
  stripe_balance_payment_debit: "transactionTypeStripeBalancePaymentDebit",
  stripe_balance_payment_debit_reversal: "transactionTypeStripeBalancePaymentDebitReversal",
  payment_network_reserve_hold: "transactionTypePaymentNetworkReserveHold",
  payment_network_reserve_release: "transactionTypePaymentNetworkReserveRelease",
  payout_minimum_balance_hold: "transactionTypePayoutMinimumBalanceHold",
  payout_minimum_balance_release: "transactionTypePayoutMinimumBalanceRelease",
  currency_conversion: "transactionTypeCurrencyConversion",
  balance_transfer_outbound: "transactionTypeBalanceTransferOutbound",
  balance_transfer_inbound: "transactionTypeBalanceTransferInbound",
  anticipation_repayment: "transactionTypeAnticipationRepayment",
  contribution: "transactionTypeContribution",
  climate_order_purchase: "transactionTypeClimateOrderPurchase",
  climate_order_refund: "transactionTypeClimateOrderRefund",
  revenue_share: "transactionTypeRevenueShare",
  obligation_outbound: "transactionTypeObligationOutbound",
  obligation_reversal_inbound: "transactionTypeObligationReversalInbound",
  issuing_authorization_hold: "transactionTypeIssuingAuthorizationHold",
  issuing_authorization_release: "transactionTypeIssuingAuthorizationRelease",
  issuing_transaction: "transactionTypeIssuingTransaction",
  issuing_dispute: "transactionTypeIssuingDispute",
};

export function getTransactionTypeLabel(type: string): string {
  if (!type) return "";
  const key = TRANSACTION_TYPE_KEYS[type.toLowerCase()];
  return key ? t(key) : "";
}
